import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react"

export interface DocsetContextType {
  selectedDocset: string
  previousDocset: string | null
  setSelectedDocset: (docset: string) => void
  onDocsetChange?: (oldDocset: string, newDocset: string) => void
}

const DocsetContext = createContext<DocsetContextType | undefined>(undefined)

interface DocsetProviderProps {
  children: ReactNode
  initialDocset?: string
  onDocsetChange?: (oldDocset: string, newDocset: string) => void
}

export function DocsetProvider({ 
  children, 
  initialDocset = "tourvisio",
  onDocsetChange
}: DocsetProviderProps) {
  const [selectedDocset, setSelectedDocsetState] = useState(initialDocset)
  const [previousDocset, setPreviousDocset] = useState<string | null>(null)

  useEffect(() => {
    if (initialDocset !== selectedDocset) {
      setSelectedDocsetState(initialDocset)
    }
  }, [initialDocset])

  const setSelectedDocset = useCallback((newDocset: string) => {
    if (newDocset === selectedDocset) return
    
    const oldDocset = selectedDocset
    setPreviousDocset(oldDocset)
    setSelectedDocsetState(newDocset)
    
    onDocsetChange?.(oldDocset, newDocset)
  }, [selectedDocset, onDocsetChange])

  const value: DocsetContextType = {
    selectedDocset,
    previousDocset,
    setSelectedDocset,
    onDocsetChange
  }

  return (
    <DocsetContext.Provider value={value}>
      {children}
    </DocsetContext.Provider>
  )
}

export function useDocset() {
  const context = useContext(DocsetContext)
  if (context === undefined) {
    throw new Error("useDocset must be used within a DocsetProvider")
  }
  return context
} 